import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  SortDirection
} from '@globaleris/ui.library'
import { Card, CardContent, CardHeader } from '@material-ui/core'
import React, { MutableRefObject, Ref, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { getCustomerDomain } from '../../../../redux'
import { Add, Delete, Edit } from '@material-ui/icons'
import { PowerModel } from '../../../../api/Argentia/model/contracts'

export type ATRVersionTableModel = {
  actions?: JSX.Element
  idDetail: number
  startDate: Date
  endDate?: Date
  tollTypeText?: string
  tollType?: number | null
  version: number[]
  period1?: number
  period2?: number
  period3?: number
  period4?: number
  period5?: number
  period6?: number
  period1id?: number
  period2id?: number
  period3id?: number
  period4id?: number
  period5id?: number
  period6id?: number
  canEdit?: boolean
  canCreateProcedure?: boolean
  powers?: PowerModel[]
}

type VersionsTableProps = {
  ref: Ref<any>
  versions: ATRVersionTableModel[]
  onInsertHandler: () => Promise<boolean>
  onEditHandler: (selectedRows: any | undefined, event: any | undefined) => void
  onDeleteHandler: (selectedRows: any | undefined, event: any | undefined) => void
  onCreateProcedureHandler: (selectedRows: any | undefined, event: any | undefined) => void
  canInsert?: boolean
}

export const VersionsTable: React.FC<VersionsTableProps> = forwardRef(
  ({
    ref,
    versions,
    onInsertHandler,
    onEditHandler,
    onDeleteHandler,
    onCreateProcedureHandler,
    canInsert
  }) => {
    const [t] = useTranslation(['contracts', 'globalerisLibrary', 'enums'])

    const tableOptions: DataTableOptions = {
      i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
      search: true,
      elevation: 0,
      customInsertEditModal: true,
      rowsPerPage: 10,
      pagination: true,
      sortOrder: { name: 'version', direction: SortDirection.DESC },
      analytics: {
        customer: getCustomerDomain(),
        view: t('contracts:components.versionsTable.analytics')
      },
      canDelete: false,
      canEdit: false,
      canInsert: canInsert,
      onRowInsert: onInsertHandler,
      rowCustomActions: [
        {
          icon: <Add fontSize="small" />,
          tooltip: t('contracts:components.versionsTable.rowCustomActions.create'),
          visibleIf: 'canCreateProcedure',
          onClick: onCreateProcedureHandler
        },
        {
          icon: <Edit fontSize="small" />,
          tooltip: t('contracts:components.versionsTable.rowCustomActions.edit'),
          visibleIf: 'canEdit',
          onClick: onEditHandler
          // userPermission: UserPermission.EDIT
        },
        {
          icon: <Delete fontSize="small" />,
          tooltip: t('contracts:components.versionsTable.rowCustomActions.delete'),
          visibleIf: 'canEdit',
          onClick: onDeleteHandler
        }
      ]
    }

    const oldVersionsColumns: DataTableColumn[] = [
      {
        name: 'ghost',
        display: false
      },
      {
        name: 'actions',
        label: '',
        hideForResponsive: true,
        type: DataType.HTML
      },
      {
        name: 'version',
        type: DataType.ARRAY,
        label: t('contracts:components.versionsTable.columns.version')
      },
      {
        name: 'tollTypeText',
        label: t('contracts:components.versionsTable.columns.tollType'),
        type: DataType.TEXT
      },
      {
        name: 'startDate',
        label: t('contracts:components.versionsTable.columns.startDate'),
        type: DataType.DATE
      },
      {
        name: 'endDate',
        label: t('contracts:components.versionsTable.columns.endDate'),
        type: DataType.DATE
      },
      {
        name: 'period1',
        label: t('contracts:components.versionsTable.columns.period1'),
        type: DataType.NUMBER,
        customDecimalScale: 3
      },
      {
        name: 'period2',
        label: t('contracts:components.versionsTable.columns.period2'),
        type: DataType.NUMBER,
        customDecimalScale: 3
      },
      {
        name: 'period3',
        label: t('contracts:components.versionsTable.columns.period3'),
        type: DataType.NUMBER,
        customDecimalScale: 3
      },
      {
        name: 'period4',
        label: t('contracts:components.versionsTable.columns.period4'),
        type: DataType.NUMBER,
        customDecimalScale: 3
      },
      {
        name: 'period5',
        label: t('contracts:components.versionsTable.columns.period5'),
        type: DataType.NUMBER,
        customDecimalScale: 3
      },
      {
        name: 'period6',
        label: t('contracts:components.versionsTable.columns.period6'),
        type: DataType.NUMBER,
        customDecimalScale: 3
      },
      {
        name: 'canEdit',
        display: false,
        type: DataType.BOOLEAN
      }
    ]

    return (
      <Card ref={ref} elevation={6}>
        <CardHeader title={t('contracts:components.versionsTable.title')}></CardHeader>
        <CardContent>
          <DataTable data={versions} columns={oldVersionsColumns} options={tableOptions} />
        </CardContent>
      </Card>
    )
  }
)
